import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Async thunk for fetching address data
export const fetchAddressData = createAsyncThunk(
  "address/fetchAddressData",
  async (_, { getState }) => {
    const state = getState();
    const userId = state.feature1.userId; // Access userId from feature1 state

    // console.log("User ID:", userId); // Log userId to verify it's available

    if (!userId) {
      throw new Error("No user ID found");
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/address/${userId}`
    );

    return response.data.data;
  }
);

// Async thunk for adding or updating an address
export const saveAddress = createAsyncThunk(
  "address/saveAddress",
  async (address, { getState }) => {
    const state = getState();
    const userId = state.feature1.userId; // Retrieve userId from feature1 state
    const { addressId, isEditing } = state.address;

    if (!userId) {
      throw new Error("No user ID found");
    }

    if (isEditing) {
      return axios
        .patch(`${process.env.REACT_APP_API_URL}/address/${addressId}`, {
          ...address,
          userId,
        })
        .then((response) => response.data);
    } else {
      return axios
        .post(`${process.env.REACT_APP_API_URL}/address`, {
          ...address,
          user_id: userId,
        })
        .then((response) => response.data);
    }
  }
);

// Async thunk for deleting an address
export const deleteAddress = createAsyncThunk(
  "address/deleteAddress",
  async (addressId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/trash/address/${addressId}`
      );
  
      return addressId; // Ensure the thunk returns the correct ID
    } catch (error) {
      console.error("Failed to delete address:", error);
      return rejectWithValue(error.response.data); // Use rejectWithValue for better error handling
    }
  }
);

const addressSlice = createSlice({
  name: "address",
  initialState: {
    addressData: [],
    formData: {
      bill_to: "",
      address: "",
      land_mark: "",
      state: "",
      city: "",
      pincode: "",
      address_type: "home",
      latitude: null,
      longitude: null,
      mobile: "",
    },
    isEditing: false,
    addressId: null,
    status: "idle",
    error: null,
  },
  reducers: {
    setEditing: (state, action) => {
      state.isEditing = action.payload.isEditing;
      state.addressId = action.payload.addressId;
      state.formData = action.payload.address || state.formData;
    },
    setFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetForm: (state) => {
      state.formData = {
        bill_to: "",
        address: "",
        land_mark: "",
        state: "",
        city: "",
        pincode: "",
        address_type: "home",
        latitude: null,
        longitude: null,
        mobile: "",
      };
      state.isEditing = false;
      state.addressId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAddressData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAddressData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.addressData = action.payload;
      })
      .addCase(fetchAddressData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(saveAddress.fulfilled, (state, action) => {
        if (state.isEditing) {
          // Update the existing address in addressData
          state.addressData = state.addressData.map((address) =>
            address._id === action.payload._id ? action.payload : address
          );
        } else {
          // Add new address
          state.addressData.push(action.payload);
        }
        state.isEditing = false; // Reset isEditing after saving
        state.addressId = null; // Clear addressId after saving
        state.formData = {
          // Clear formData after saving
          bill_to: "",
          address: "",
          land_mark: "",
          state: "",
          city: "",
          pincode: "",
          address_type: "home",
          latitude: null,
          longitude: null,
          phone: "",
          isDeleted: false,
        };
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.addressData = state.addressData.filter(
          (address) => address._id !== action.payload
        );
      });
  },
});

export const { setEditing, setFormData, resetForm } = addressSlice.actions;
export default addressSlice.reducer;
